// AssociatesPage.js
import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import PageHero from './PageHero';
import { getAsociadosPublic } from '../services/clubsService';
import ContactoAsociado from './ContactoAsociado';
import { getAllCMSImages } from '../services/s3Service';
import { getFirstCachedImage } from '../utils/cmsCache';

const AssociatesPage = () => {
  const [tipo, setTipo] = useState('todos');
  const [region, setRegion] = useState('todas');
  const [busqueda, setBusqueda] = useState('');
  const [heroImage, setHeroImage] = useState(() => getFirstCachedImage(['page_hero_associates']));
  const [asociados, setAsociados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getAllCMSImages().then((imgs) => setHeroImage(imgs.page_hero_associates || null))
      .catch(() => setHeroImage(null));
  }, []);

  // Tipos de asociados
  const tipos = [
    { id: 'todos', nombre: 'Todos' },
    { id: 'gimnasio', nombre: 'Gimnasio' },
    { id: 'club', nombre: 'Club' }
  ];

  // Regiones disponibles (igual que en formularios de club/gym)
  const regiones = [
    'todas',
    'Arica y Parinacota',
    'Tarapacá',
    'Antofagasta',
    'Atacama',
    'Coquimbo',
    'Valparaíso',
    'Metropolitana',
    "O'Higgins",
    'Maule',
    'Ñuble',
    'Biobío',
    'Araucanía',
    'Los Ríos',
    'Los Lagos',
    'Aysén',
    'Magallanes'
  ];

  // Normalizar texto para comparar región (quitar acentos, espacios, mayúsculas)
  const normalizarRegion = (texto) => {
    if (!texto || typeof texto !== 'string') return '';
    return texto
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/\u0301/g, '')  // acento agudo
      .replace(/[\u0300-\u036f]/g, '')  // resto de diacríticos
      .replace(/\s+/g, ' ');
  };

  // Cargar asociados desde BD al montar
  useEffect(() => {
    let cancelled = false;
    setCargando(true);
    setError(null);
    getAsociadosPublic()
      .then((lista) => {
        if (!cancelled) setAsociados(lista);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Error al cargar asociados');
      })
      .finally(() => {
        if (!cancelled) setCargando(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Filtrar asociados
  const filtrarAsociados = () => {
    return asociados.filter(asociado => {
      if (tipo !== 'todos' && asociado.tipo !== tipo) return false;
      if (region !== 'todas') {
        const regionNorm = normalizarRegion(region);
        const asociadoRegionNorm = normalizarRegion(asociado.region);
        if (!asociadoRegionNorm || regionNorm !== asociadoRegionNorm) return false;
      }
      if (busqueda) {
        const q = busqueda.toLowerCase();
        const matchNombre = (asociado.nombre || '').toLowerCase().includes(q);
        const matchCiudad = (asociado.ciudad || '').toLowerCase().includes(q);
        if (!matchNombre && !matchCiudad) return false;
      }
      return true;
    });
  };

  const asociadosFiltrados = filtrarAsociados();

  // Resetear filtros
  const resetearFiltros = () => {
    setTipo('todos');
    setRegion('todas');
    setBusqueda('');
  };

  // Colores minimalistas
  const colors = {
    primary: '#1a1a1a',
    secondary: '#666',
    accent: '#FF4500',
    lightGray: '#f8f9fa',
    mediumGray: '#eaeaea',
    darkGray: '#333',
    white: '#ffffff',
    hover: '#e63e00',
  };

  return (
    <div style={{ 
      fontFamily: 'Arial, sans-serif', 
      width: '100%', 
      margin: '0', 
      backgroundColor: colors.white, 
      minHeight: '100vh',
      color: colors.primary
    }}>
      <Header />
      <PageHero
        eyebrow="Comunidad"
        imageUrl={heroImage}
        title="Asociados"
        subtitle="Encuentra gimnasios y clubes asociados a ACHIPO en todo Chile"
      />
      <div className="page-content-wrap" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
        {/* Panel de Filtros */}
        <div style={{
          backgroundColor: colors.lightGray,
          padding: '25px',
          borderRadius: '8px',
          marginBottom: '30px',
          marginTop: '-2cm',
          border: `1px solid ${colors.mediumGray}`
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            borderBottom: `1px solid ${colors.mediumGray}`,
            paddingBottom: '15px'
          }}>
            <h3 style={{
              fontSize: '1.1em',
              fontWeight: '600',
              margin: 0
            }}>
              FILTROS DE BÚSQUEDA
            </h3>
            <button 
              onClick={resetearFiltros}
              style={{
                backgroundColor: 'transparent',
                color: '#1a4d8c',
                border: `1px solid #1a4d8c`,
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '0.9em',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textTransform: 'uppercase'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#1a4d8c';
                e.target.style.color = colors.white;
                e.target.style.borderColor = '#1a4d8c';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#1a4d8c';
                e.target.style.borderColor = '#1a4d8c';
              }}
            >
              RESTABLECER
            </button>
          </div>

          {/* Filtros en línea */}
          <div style={{ 
            display: 'flex',
            flexWrap: 'wrap',
            gap: '15px',
            alignItems: 'center'
          }}>
            {/* Tipo */}
            <div>
              <label style={{ 
                display: 'block', 
                color: colors.secondary, 
                fontSize: '0.9em',
                marginBottom: '6px',
                fontWeight: 'bold',
                textTransform: 'uppercase'
              }}>
                Tipo
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {tipos.filter(t => t.id === 'todos').map(t => (
                    <button
                      key={t.id}
                      onClick={() => setTipo(t.id)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: tipo === t.id ? '#1a4d8c' : colors.lightGray,
                        color: tipo === t.id ? colors.white : colors.secondary,
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        fontWeight: tipo === t.id ? '600' : '500',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {t.nombre}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {tipos.filter(t => t.id === 'gimnasio').map(t => (
                    <button
                      key={t.id}
                      onClick={() => setTipo(t.id)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: tipo === t.id ? '#1a4d8c' : colors.lightGray,
                        color: tipo === t.id ? colors.white : colors.secondary,
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        fontWeight: tipo === t.id ? '600' : '500',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {t.nombre}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {tipos.filter(t => t.id === 'club').map(t => (
                    <button
                      key={t.id}
                      onClick={() => setTipo(t.id)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: tipo === t.id ? '#1a4d8c' : colors.lightGray,
                        color: tipo === t.id ? colors.white : colors.secondary,
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        fontWeight: tipo === t.id ? '600' : '500',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {t.nombre}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Separador */}
            <div style={{
              width: '1px',
              height: '50px',
              backgroundColor: '#1a4d8c',
              alignSelf: 'center'
            }}></div>

            {/* Región */}
            <div>
              <label style={{ 
                display: 'block', 
                color: colors.secondary, 
                fontSize: '0.9em',
                marginBottom: '6px',
                fontWeight: 'bold',
                textTransform: 'uppercase'
              }}>
                Región
              </label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: `1px solid #1a4d8c`,
                  borderRadius: '4px',
                  backgroundColor: colors.white,
                  color: '#1a4d8c',
                  fontSize: '0.9em',
                  cursor: 'pointer',
                  minWidth: '200px'
                }}
              >
                {regiones.map(r => (
                  <option key={r} value={r}>
                    {r === 'todas' ? 'Todas las regiones' : r}
                  </option>
                ))}
              </select>
            </div>

            {/* Separador */}
            <div style={{
              width: '1px',
              height: '50px',
              backgroundColor: '#1a4d8c',
              alignSelf: 'center'
            }}></div>

            {/* Búsqueda */}
            <div>
              <label style={{ 
                display: 'block', 
                color: colors.secondary, 
                fontSize: '0.9em',
                marginBottom: '6px',
                fontWeight: 'bold',
                textTransform: 'uppercase'
              }}>
                Buscar
              </label>
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Nombre o ciudad..."
                style={{
                  padding: '8px 12px',
                  border: `1px solid #1a4d8c`,
                  borderRadius: '4px',
                  backgroundColor: colors.white,
                  color: colors.primary,
                  fontSize: '0.9em',
                  minWidth: '200px'
                }}
              />
            </div>
          </div>
        </div>

        {/* Resultados */}
        <div style={{ 
          marginTop: '20px'
        }}>
          {cargando ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: colors.secondary }}>
              <p style={{ fontSize: '1.1em' }}>Cargando asociados...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: colors.secondary }}>
              <p style={{ fontSize: '1.1em' }}>{error}</p>
            </div>
          ) : asociadosFiltrados.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
              gap: '20px'
            }}>
              {asociadosFiltrados.map(asociado => (
                <div
                  key={asociado.id}
                  style={{
                    backgroundColor: colors.white,
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '8px',
                    padding: '25px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    marginBottom: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px'
                  }}>
                    {asociado.logo_url ? (
                      <img
                        src={asociado.logo_url}
                        alt={`Logo ${asociado.nombre}`}
                        loading="lazy"
                        style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', border: `1px solid ${colors.mediumGray}`, flexShrink: 0 }}
                      />
                    ) : (
                      <span
                        aria-hidden="true"
                        className="ach-display"
                        style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#111', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3em', flexShrink: 0 }}
                      >
                        {String(asociado.nombre || '?').charAt(0)}
                      </span>
                    )}
                    <h3 className="ach-display" style={{
                      fontSize: '1.05em',
                      color: colors.primary,
                      margin: 0,
                      minWidth: 0,
                      lineHeight: 1.25,
                      overflowWrap: 'anywhere'
                    }}>
                      {asociado.nombre}
                    </h3>
                  </div>
                  <span style={{
                    position: 'absolute',
                    bottom: '10px',
                    right: '10px',
                    backgroundColor: asociado.tipo === 'gimnasio' ? '#0039A6' : '#D52B1E',
                    color: colors.white,
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.75em',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    zIndex: 1
                  }}>
                    {asociado.tipo === 'gimnasio' ? 'Gimnasio' : 'Club'}
                  </span>

                  <div style={{ marginBottom: '15px' }}>
                    <p style={{
                      color: colors.secondary,
                      fontSize: '0.9em',
                      margin: '5px 0'
                    }}>
                      <strong>📍</strong> {[asociado.ciudad, asociado.region].filter(Boolean).join(', ') || 'Sin ubicación'}
                    </p>
                    <ContactoAsociado asociado={asociado} />
                  </div>

                  <div style={{
                    borderTop: `1px solid ${colors.mediumGray}`,
                    paddingTop: '15px'
                  }}>
                    {(asociado.servicios && asociado.servicios.length > 0) && (
                      <>
                        <p style={{
                          fontSize: '0.85em',
                          fontWeight: '600',
                          color: colors.primary,
                          marginBottom: '8px'
                        }}>
                          Servicios:
                        </p>
                        <ul style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0
                        }}>
                      {(asociado.servicios || []).map((servicio, index) => (
                        <li key={index} style={{
                          color: colors.secondary,
                          fontSize: '0.85em',
                          marginBottom: '5px',
                          paddingLeft: '15px',
                          position: 'relative'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            color: '#1a4d8c'
                          }}>•</span>
                          {servicio}
                        </li>
                      ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: colors.secondary
            }}>
              <p style={{ fontSize: '1.1em' }}>
                No se encontraron asociados que coincidan con los filtros seleccionados.
              </p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AssociatesPage;
